// La progression : paliers de liberté, objectifs d'étape et changements d'étape.
import { housingCost } from './property';
import type { TextKey } from '../content/fr';
import { PATHS } from '../content/paths';
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import { t } from '../engine/i18n';
import type { GameState, ObjectiveId, PalierId } from '../engine/state';
import { addJournal, addNotice, unlockPage } from './actions';
import { startingSalary, totalLevels } from './career';
import { emergencyTarget, monthlySpending, networth, passiveIncomeAverage, startDeferredLoans } from './economy';
import { setMutuelle } from './health';
import { hasMarketShares } from './av';
import { peaMonthlyAmount } from './pea';
import { depositToSavings, regulatedSavings } from './livret';

export interface PalierDef {
  id: PalierId;
  label: TextKey;
  /** Le libellé pour un propriétaire, s'il diffère. */
  ownerLabel?: TextKey;
  /** Revenus passifs mensuels nécessaires pour atteindre le palier. */
  threshold: (state: GameState) => number;
}

export const PALIERS: PalierDef[] = [
  { id: 'cafe', label: 'palier.cafe', threshold: () => 8 },
  { id: 'forfait', label: 'palier.forfait', threshold: (state) => state.expenses.forfait?.amount ?? 15 },
  {
    id: 'courses',
    label: 'palier.courses',
    threshold: (state) => round2((state.expenses.courses?.amount ?? 250) / 4.33),
  },
  // Le loyer, ou pour un propriétaire le crédit, la copropriété et la taxe foncière.
  { id: 'loyer', label: 'palier.loyer', ownerLabel: 'palier.logement', threshold: housingCost },
  { id: 'toutes', label: 'palier.toutes', threshold: (state) => monthlySpending(state) },
];

export function palierLabel(state: GameState, palier: PalierDef): TextKey {
  return state.housing === 'proprio' && palier.ownerLabel ? palier.ownerLabel : palier.label;
}

export function nextPalier(state: GameState): PalierDef | null {
  return PALIERS.find((palier) => !state.paliers.includes(palier.id)) ?? null;
}

export function checkPaliers(state: GameState): void {
  // La tendance sur un an : un bon mois de bourse ne suffit pas à franchir un palier.
  const passive = passiveIncomeAverage(state);
  for (const palier of PALIERS) {
    if (state.paliers.includes(palier.id)) continue;
    if (passive < palier.threshold(state)) break;
    state.paliers.push(palier.id);
    addNotice(state, 'palier.reached', { label: t(palierLabel(state, palier)) });
    addJournal(state, 'journal.palier', { label: t(palierLabel(state, palier)) });
  }
}

export interface ObjectiveDef {
  id: ObjectiveId;
  label: TextKey;
  check: (state: GameState) => boolean;
}

/** Objectifs des années d'études ou d'alternance : ils montrent quoi faire dès le départ. */
export const PROLOGUE_OBJECTIVES: ObjectiveDef[] = [
  { id: 'openLivret', label: 'obj.openLivret', check: (state) => state.livret > 0 },
  {
    id: 'firstSaving',
    label: 'obj.firstSaving',
    check: (state) => Object.values(state.expenses).some((expense) => (expense?.saved ?? 0) > 0),
  },
  {
    id: 'firstUpgrade',
    label: 'obj.firstUpgrade',
    check: (state) => state.upgrades.some((id) => id !== 'telephone'),
  },
  {
    id: 'skillLevel2',
    label: 'obj.skillLevel2',
    check: (state) => Object.values(state.skills).some((skill) => skill.level >= 2),
  },
  { id: 'savings1000', label: 'obj.savings1000', check: (state) => state.livret >= 1000 },
];

export const STAGE1_OBJECTIVES: ObjectiveDef[] = [
  { id: 'noOverdraft', label: 'obj.noOverdraft', check: (state) => state.flags.monthWithoutOverdraft === true },
  { id: 'autoSaving', label: 'obj.autoSaving', check: (state) => state.budget.autoSaving > 0 },
  {
    id: 'emergency',
    label: 'obj.emergency',
    check: (state) => emergencyTarget(state) > 0 && regulatedSavings(state) >= emergencyTarget(state),
  },
  { id: 'cafe', label: 'obj.cafe', check: (state) => state.paliers.includes('cafe') },
];

/** Objectifs de l'étape 2 : placer en bourse, régulièrement, et tenir pendant une baisse. */
export const STAGE2_OBJECTIVES: ObjectiveDef[] = [
  { id: 'firstInvestment', label: 'obj.firstInvestment', check: hasMarketShares },
  { id: 'monthlyInvestment', label: 'obj.monthlyInvestment', check: (state) => peaMonthlyAmount(state) > 0 },
  { id: 'holdDrop', label: 'obj.holdDrop', check: (state) => state.flags.heldThroughDrop === true },
];

export function currentObjectives(state: GameState): ObjectiveDef[] {
  if (state.stage === 'stage2') return STAGE2_OBJECTIVES;
  if (state.stage !== 'prologue') return STAGE1_OBJECTIVES;
  return state.prologueStep === 'years' ? PROLOGUE_OBJECTIVES : [];
}

export function checkObjectives(state: GameState): void {
  for (const objective of currentObjectives(state)) {
    if (state.objectivesDone.includes(objective.id) || !objective.check(state)) continue;
    state.objectivesDone.push(objective.id);
    addNotice(state, 'obj.done', { label: t(objective.label) });
  }
  // L'étape 1 se termine quand le fonds d'urgence est complet.
  if (state.stage === 'stage1' && state.objectivesDone.includes('emergency')) {
    state.stage = 'stage2';
    state.stageIntro = 'stage2';
    addJournal(state, 'journal.stage2');
  }
}

/** Le premier emploi à plein temps : début de l'étape 1. */
export function startFirstJob(state: GameState): void {
  const path = PATHS[state.path ?? 'work'];
  state.stage = 'stage1';
  state.firstJobDay = state.day;
  state.firstJobTargetDay = null;
  // Exclu de l'école : le métier trouvé correspond au niveau d'études déjà validé.
  const baseSalary = state.jobSalaryBase ?? path.salary;
  state.salary = startingSalary(state, baseSalary);
  state.jobSalaryBase = null;
  // Les niveaux des années d'avant comptent dans le salaire de départ, pas dans les augmentations ni les promotions.
  state.levelsAtHire = totalLevels(state);
  state.levelsAtLastRaise = state.levelsAtHire;
  state.yearsWithoutRaise = 0;
  // Les prêts étudiants en différé total : leur dette a grossi pendant les études, le remboursement commence.
  startDeferredLoans(state);
  state.studentIncome = 0;
  state.parentsHelp = 0;
  if (state.expenses.courses) state.expenses.courses.amount = Math.max(state.expenses.courses.amount, 250);
  // Au travail, il faut une tenue correcte : les frais personnels montent un peu.
  if (state.expenses.personnel) state.expenses.personnel.amount = Math.max(state.expenses.personnel.amount, 90);
  if (state.expenses.transport && !state.hasCar) {
    state.expenses.transport.amount = Math.max(state.expenses.transport.amount, 60);
  }
  if (state.budget.leisure < 150) state.budget.leisure = 150;
  // La mutuelle d'entreprise, obligatoire, remplace la mutuelle étudiante : l'employeur en paie la moitié.
  if (state.expenses.mutuelle) {
    setMutuelle(state, state.mutuelleLevel);
    addJournal(state, 'journal.mutuelleEntreprise', { amount: fmtEur(state.expenses.mutuelle.amount) });
  }
  if (state.piggy > 0) {
    // La tirelire rejoint le Livret A, dans la limite de son plafond.
    const moved = depositToSavings(state, state.piggy);
    state.piggy = round2(state.piggy - moved);
    if (moved > 0) addJournal(state, 'journal.piggyToLivret', { amount: fmtEur(moved) });
  }
  state.year = { startDay: state.day, networthStart: networth(state), income: 0, saved: 0 };
  state.lastEventDay = state.day;
  state.stageIntro = 'stage1';
  unlockPage(state, 'budget');
  addJournal(state, 'journal.firstJob', { salary: fmtEur(state.salary) });
}
